import { Image } from '@components';
import { useRockHallTV } from '@/providers/RockHallTVContext';
import { getBooleanParam } from '@/utils/urlParams';
import { useSoundContext } from '@/providers/SoundContext';
import { useGlobalVolume } from '@/hooks/useGlobalVolume';
import gsap from 'gsap';
import { INTER_OBS_CONFIG } from '../../constants';

import styles from './RHTV-ChannelItem.module.scss';
import className from 'classnames/bind';
const cx = className.bind(styles);

import { useDebug } from "@/utils/useDebugLog";
import { useState, useRef, useCallback, useEffect, memo } from 'react';
import { replaceStringVar } from '@/core/utils';
const { log } = useDebug({ name: 'RHTV_ChannelItem', bgColor: '#F2668B', enabled: false });

// ----------------------------------------

type RockHallTvChannelProps = RockHallTvChannel & {
  index?: number;
  onChannelClick?: (channel: RockHallTvChannel, index: number) => void;
  onChannelFocus?: (index: number, element: HTMLElement) => void;
  onTriggerKick?: () => void;
  isKickComplete?: boolean; // State 2: kick animation completed
  showModal?: boolean; // State 3: modal is open (from parent)
  isPortraitMode?: boolean;
  maxItemSize?: { width: number; height: number } | null; // Cap channel item size
  isTouchDevice?: boolean; // whether device supports touch
  sharedMinFontSize?: number | null; // minimum font-size across all channel items
  channelLabel: string;
  onFontSizeCalculated?: (fontSize: number, channelIndex: number) => void; // callback when font-size is calculated
  shouldLoadVideo?: boolean; // whether this channel should load its video
};

const Channel = memo(function Channel({ rhtvChannelContent, title, slug, uri, index,
  onChannelClick,
  onChannelFocus,
  onTriggerKick,
  isKickComplete = false,
  showModal: showModalProp = false,
  isPortraitMode, maxItemSize, isTouchDevice = false,
  sharedMinFontSize = null,
  onFontSizeCalculated,
  channelLabel,
  shouldLoadVideo = false
}: RockHallTvChannelProps) {

  const isDebugMode = getBooleanParam('debug');
  const { rrtvSoundEnabled, globalVolume } = useSoundContext();
  const { showModal: showModalContext } = useRockHallTV();

  const channelTitle = replaceStringVar(channelLabel, { title });

  // Use prop if provided, otherwise fall back to context
  const showModal = showModalProp || showModalContext;

  const { thumbnail, previewVideo } = rhtvChannelContent || {};
  const [videoReady, setVideoReady] = useState(false);
  const hasCalledVideoReadyRef = useRef(false);

  const isHoveringRef = useRef(false);
  const videoRef = useGlobalVolume<HTMLVideoElement>(`rhtv-channel-${slug || index}`);
  const containerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLSpanElement>(null);
  const volumeTweenRef = useRef<gsap.core.Tween | null>(null);
  const volumeTweenDuration = 0.26;
  const onFontSizeCalculatedRef = useRef(onFontSizeCalculated);

  // DEBUG: Check video loading state every second
  useEffect(() => {
    if (!shouldLoadVideo || !videoRef.current) return;

    const video = videoRef.current;

    // Check if video is already loaded (events may have been missed)
    // readyState 4 = HAVE_ENOUGH_DATA (can play through without buffering)
    if (video.readyState === 4 && !hasCalledVideoReadyRef.current) {
      // console.log(`⚠️ Video ${index} already loaded (readyState=4) but events didn't fire! Manually triggering.`);
      hasCalledVideoReadyRef.current = true;
      setVideoReady(true);
    }

    /* Debug logging of video state
    const interval = setInterval(() => {
      if (!video) return;
      console.log(`📹 Video ${index} state:`, {
        readyState: video.readyState,
        networkState: video.networkState,
        src: video.src,
        currentTime: video.currentTime,
        duration: video.duration,
        paused: video.paused
      });
    }, 1000); */

    // return () => clearInterval(interval);
  }, [shouldLoadVideo, index]);

  // Keep ref up to date
  useEffect(() => {
    onFontSizeCalculatedRef.current = onFontSizeCalculated;
  }, [onFontSizeCalculated]);

  // -------------------- Pause videos when modal is open

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (showModal) {
      // Modal is open - pause the video and mute it
      video.pause();
      video.muted = true;
      video.volume = 0;

      // Kill any ongoing volume tweens
      if (volumeTweenRef.current) {
        volumeTweenRef.current.kill();
      }
    }
    // Note: Don't auto-resume when modal closes - let intersection observer handle it
  }, [showModal]);

  // -------------------- Intersection Observer: Pause offscreen videos

  useEffect(() => {
    const video = videoRef.current;
    const container = containerRef.current;

    if (!video || !container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Video is visible - play it (but only if modal is not open)
            if (!showModal) {
              video.play().catch(() => {
                // Ignore play errors (e.g., if already playing)
              });
            }
          } else {
            // Video is offscreen - pause it
            video.pause();
          }
        });
      },
      {
        rootMargin: INTER_OBS_CONFIG.ROOT_MARGIN,
        threshold: INTER_OBS_CONFIG.THRESHOLD
      }
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [previewVideo, showModal]);

  // -------------------- Handlers

  const handleClicked = useCallback((e: React.MouseEvent) => {
    // Ignore right-click and middle-click
    if (e.button !== 0) return;

    // log('handleClicked', e.button, { title, slug, index, isKickComplete });

    // STATE 1: Pre-kick - trigger kick animation instead of opening modal
    if (!isKickComplete) {
      // log('Pre-kick state - triggering kick animation');
      onTriggerKick?.();
      return;
    }

    // STATE 2: Post-kick - open modal normally
    // Call the parent's callback if provided
    if (onChannelClick) {
      onChannelClick({ rhtvChannelContent, title, slug, uri }, index ?? 0);
    }
  }, [onChannelClick, onTriggerKick, rhtvChannelContent, title, slug, uri, index, isKickComplete]);

  const handleMouseEnter = useCallback(() => {
    isHoveringRef.current = true;
    const video = videoRef.current;

    // Don't unmute if modal is open
    if (video && rrtvSoundEnabled && !showModal) {
      // Kill any existing tween
      if (volumeTweenRef.current) {
        volumeTweenRef.current.kill();
      }

      // Unmute and fade in volume
      video.muted = false;

      volumeTweenRef.current = gsap.to(video, {
        volume: globalVolume,
        duration: volumeTweenDuration,
        ease: 'power1.out'
      });
    }
  }, [rrtvSoundEnabled, globalVolume, showModal]);

  const handleMouseLeave = useCallback(() => {
    isHoveringRef.current = false;
    const video = videoRef.current;

    if (video) {
      // Kill any existing tween
      if (volumeTweenRef.current) {
        volumeTweenRef.current.kill();
      }

      // Fade out volume then mute
      volumeTweenRef.current = gsap.to(video, {
        volume: 0,
        duration: volumeTweenDuration,
        ease: 'power1.out',
        onComplete: () => {
          if (video) {
            video.muted = true;
          }
        }
      });
    }
  }, []);

  const handleLoadedData = useCallback(() => {
    // log(`🟢 handleLoadedData for channel ${index}`, { hasCalledVideoReadyRef: hasCalledVideoReadyRef.current });
    if (hasCalledVideoReadyRef.current) return; // Already handled
    log(`handleLoadedData fired for channel ${index} (${title})`);
    hasCalledVideoReadyRef.current = true;
    setVideoReady(true);
  }, [index, title]);

  const handleLoadedMetadata = useCallback(() => {
    // log(`🟡 handleLoadedMetadata for channel ${index}`, { hasCalledVideoReadyRef: hasCalledVideoReadyRef.current });
    if (hasCalledVideoReadyRef.current) return; // Already handled
    log(`handleLoadedMetadata fired for channel ${index} (${title})`);
    hasCalledVideoReadyRef.current = true;
    setVideoReady(true);
  }, [index, title]);

  const handleCanPlay = useCallback(() => {
    // log(`🔵 handleCanPlay for channel ${index}`, { hasCalledVideoReadyRef: hasCalledVideoReadyRef.current });
    if (hasCalledVideoReadyRef.current) return; // Already handled
    log(`handleCanPlay fired for channel ${index} (${title})`);
    hasCalledVideoReadyRef.current = true;
    setVideoReady(true);
  }, [index, title]);

  const handleVideoError = useCallback((e: React.SyntheticEvent<HTMLVideoElement>) => {
    log(`Video error for channel ${index} (${title})`, e);
  }, [index, title]);

  // Sync muted state when soundEnabled changes globally
  useEffect(() => {
    const video = videoRef.current;

    if (video && !isHoveringRef.current) {
      video.muted = true;
      video.volume = 0;
    } else if (video && isHoveringRef.current) {
      if (rrtvSoundEnabled) {
        video.muted = false;
        // Immediately set to global volume if hovering and sound is enabled
        video.volume = globalVolume;
      } else {
        video.muted = true;
        video.volume = 0;
      }
    }
  }, [rrtvSoundEnabled, globalVolume]);

  // Cleanup
  useEffect(() => {
    return () => {
      // Cleanup volume tween
      if (volumeTweenRef.current) {
        volumeTweenRef.current.kill();
      }
    };
  }, []);

  // -------------------- Title Overflow Detection & Scaling

  useEffect(() => {
    const titleElement = titleRef.current;
    if (!titleElement) return;

    const checkAndScaleTitle = () => {
      // Get the container (content-foreground) width
      const container = titleElement.parentElement;
      if (!container) return;

      const containerWidth = container.offsetWidth;

      // PHASE 1: If we have a shared minimum font-size from parent, use that directly
      if (sharedMinFontSize !== null) {
        titleElement.style.fontSize = `${sharedMinFontSize}px`;
        return; // Don't report back to parent when using shared min (avoids infinite loop)
      }

      // PHASE 2: Calculate the required font-size for this title independently
      // Reset to computed style to get the original font-size from CSS
      titleElement.style.fontSize = '';

      // Get the current computed font-size
      const computedStyle = window.getComputedStyle(titleElement);
      let currentFontSize = parseFloat(computedStyle.fontSize);

      // Get the natural text width (scrollWidth = actual content width)
      let textWidth = titleElement.scrollWidth;

      // If text overflows, reduce font-size until it fits
      if (textWidth > containerWidth) {
        const maxIterations = 20; // Prevent infinite loop
        let iterations = 0;

        while (textWidth > containerWidth && iterations < maxIterations) {
          // Reduce font-size by 5% each iteration
          currentFontSize *= 0.95;
          titleElement.style.fontSize = `${currentFontSize}px`;

          // Re-measure after font-size change
          textWidth = titleElement.scrollWidth;
          iterations++;
        }
      }

      // Report the calculated font-size back to parent
      if (onFontSizeCalculatedRef.current && index !== undefined) {
        onFontSizeCalculatedRef.current(currentFontSize, index);
      }
    };

    // Run immediately
    checkAndScaleTitle();

    // Use ResizeObserver to detect size changes
    const resizeObserver = new ResizeObserver(() => {
      checkAndScaleTitle();
    });

    if (titleElement.parentElement) {
      resizeObserver.observe(titleElement.parentElement);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [maxItemSize, title, sharedMinFontSize, index]);

  // --------------------

  log('ChannelItem Render', { index, title, shouldLoadVideo, videoReady, previewVideo: !!previewVideo });

  // Keyboard handler for accessibility
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();

      // STATE 1: Pre-kick - trigger kick animation instead of opening modal
      if (!isKickComplete) {
        if (onTriggerKick) {
          onTriggerKick();
        }
        return;
      }

      // STATE 2: Post-kick - activate channel normally
      if (onChannelClick && index !== undefined) {
        const channel: RockHallTvChannel = { rhtvChannelContent, title, slug, uri };
        onChannelClick(channel, index);
      }
    }
  };

  // Track if the last interaction was via mouse (to prevent onFocus from triggering on click)
  const isMouseInteractionRef = useRef(false);

  // Focus handler to scroll channel into view - only for keyboard navigation
  const handleFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    // Only trigger scroll-into-view if focus came from keyboard (Tab), not mouse click
    if (!isMouseInteractionRef.current && onChannelFocus && index !== undefined && containerRef.current) {
      onChannelFocus(index, containerRef.current);
    }
    // Reset the flag
    isMouseInteractionRef.current = false;
  };

  // Calculate focusability based on state
  // STATE 1 (pre-kick): focusable
  // STATE 2 (post-kick): focusable
  // STATE 3 (modal open): NOT focusable
  const isFocusable = !showModal;

  // Track mouse interactions to prevent onFocus from triggering scroll-into-view
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    isMouseInteractionRef.current = true;
    e.stopPropagation();
  }, []);

  // Apply fixed width and height based on modal player size
  const containerStyle = maxItemSize ? {
    width: `${maxItemSize.width}px`,
    height: `${maxItemSize.height}px`,
  } : {};

  // -------------------- Render

  return (
    <div
      ref={containerRef}
      className={`${cx(["channel", { debug: isDebugMode }, { portrait: isPortraitMode }])} channel-${index} channel-item`}
      style={containerStyle}
      onClick={handleClicked}
      {...(!isTouchDevice && {
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
      })}
      onMouseDown={handleMouseDown}
      role="button"
      tabIndex={isFocusable ? 0 : -1}
      aria-label={channelTitle}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
    >
      <div className={cx("media-background")}>
        <Image className={cx("thumbnail")} image={thumbnail} />
        {previewVideo && shouldLoadVideo && (
          <>
            {log(`Rendering video element for channel ${index}, videoReady=${videoReady}`)}
            <video
              ref={videoRef}
              className={cx("preview-video")}
              style={{
                opacity: videoReady ? 1 : 0,
                transition: 'opacity 0.5s ease-out'
              }}
              src={previewVideo}
              autoPlay
              muted={true}
              loop
              playsInline
              preload="auto"
              onLoadedMetadata={handleLoadedMetadata}
              onLoadedData={handleLoadedData}
              onCanPlay={handleCanPlay}
              onError={handleVideoError}
            />
          </>
        )}
      </div>
      <div className={cx("content-foreground")}>
        <span ref={titleRef} className={`${cx("title")} channel-title`}>{title}</span>
      </div>
    </div>
  );

});

export default Channel;
